import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Video,
  MessageCircle,
  Award,
  Users,
  Zap,
  Target,
  Sparkles,
  Trophy,
  Play,
  Check,
  Star,
  Camera,
  Eye,
  Radio,
  Compass,
  Crown,
  Timer,
  Apple,
  Dumbbell,
  FlaskConical,
  ClipboardCheck,
} from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { buildWhatsAppLink, WHATSAPP_NUTRITION_MESSAGE } from "@/components/FloatingWhatsAppButton";
import WhoThisIsForSection from "@/components/WhoThisIsForSection";
import CoachingProgramsSection from "@/components/CoachingProgramsSection";

const HomePage = () => {
  const waLink = buildWhatsAppLink();
  const waNutritionLink = buildWhatsAppLink(WHATSAPP_NUTRITION_MESSAGE);

  return (
    <div className="bg-black min-h-screen">
      {/* ============ HERO — CINEMATIC VIDEO BACKGROUND ============ */}
      <section
        className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Video — cross-browser autoplay (iOS, Android, Chrome, Safari) */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          poster="/assets/images/v500z5es_dac32791-238d-44a6-a46c-4bf04f48455b.jpeg"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          aria-hidden="true"
        >
          <source src="/assets/images/hero-video-1.mp4" type="video/mp4" />
          <source src="/assets/images/hero-video-2.mp4" type="video/mp4" />
          <source src="/assets/images/hero-video-1.mov" type="video/quicktime" />
        </video>

        {/* Layered gradients for cinematic depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,rgba(0,0,0,0.75)_100%)]" />
        {/* subtle gold vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(212,175,55,0.10)_0%,transparent_55%)]" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 border border-[#D4AF37]/60 bg-black/50 backdrop-blur-md px-5 py-2 mb-8 sm:mb-10"
            >
              <Trophy className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-semibold">
                Camp Cormier Academy · Elite Coaching
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-[2.75rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl text-white mb-6 sm:mb-8 tracking-tight [text-shadow:0_2px_30px_rgba(0,0,0,0.6)]"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              TRAIN WITH <br className="hidden sm:block" />
              <span className="text-[#D4AF37]">CHRIS CORMIER</span>
              <span className="block mt-2 sm:mt-3">THE REAL DEAL</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="text-white/85 text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-10 sm:mb-14 [text-shadow:0_1px_20px_rgba(0,0,0,0.6)]"
            >
              <span className="text-[#D4AF37] font-semibold">40+ Years</span> of Professional
              Bodybuilding Experience. One Proven System.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-center"
            >
              <a
                href="#who-this-is-for"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("who-this-is-for")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-7 sm:px-9 py-4 sm:py-[18px] text-[11px] sm:text-xs tracking-[0.28em] uppercase font-bold hover:bg-white transition-all duration-300 shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] hover:shadow-[0_18px_48px_-8px_rgba(212,175,55,0.75)] cursor-pointer"
                data-testid="hero-who-btn"
              >
                <Trophy size={16} />
                Is This For You?
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#coaching-programs"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("coaching-programs")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center justify-center gap-3 border border-white/30 text-white hover:bg-white hover:text-black px-7 sm:px-9 py-4 sm:py-[18px] text-[11px] sm:text-xs tracking-[0.28em] uppercase font-bold transition-all duration-300 backdrop-blur-sm cursor-pointer"
                data-testid="hero-programs-btn"
              >
                View Coaching Programs
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/40">
          <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37] to-transparent" />
        </div>
      </section>

      {/* ============ CREDIBILITY STRIP ============ */}
      <section className="border-y border-[#1A1A1A] bg-black py-10 sm:py-14" data-testid="credibility-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          {[
            { stat: "40+", label: "Years Experience" },
            { stat: "500+", label: "Athletes Coached" },
            { stat: "2X", label: "Mr. Olympia Top 3" },
            { stat: "4X", label: "Ironman Pro Champion" },
          ].map((c) => (
            <div key={c.label} data-testid={`cred-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p
                className="text-[#D4AF37] font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-2 tracking-tight"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {c.stat}
              </p>
              <p className="text-white/70 text-[10px] sm:text-xs tracking-[0.25em] uppercase font-semibold">
                {c.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ ELITE COACHING — AUTHORITY STRIP ============ */}
      <section className="bg-black py-12 border-y border-[#1A1A1A]" data-testid="authority-strip">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Trophy, label: "IFBB LEGEND" },
            { icon: Crown, label: "OLYMPIA VETERAN" },
            { icon: Users, label: "ATHLETES WORLDWIDE" },
            { icon: Award, label: "ELITE-LEVEL COACHING" },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-3 justify-center lg:justify-start">
              <it.icon className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
              <span className="text-white/80 text-[10px] sm:text-xs tracking-[0.25em] uppercase">
                {it.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WHO THIS IS FOR ============ */}
      <WhoThisIsForSection />

      {/* ============ COACHING PROGRAMS / PRICING ============ */}
      <CoachingProgramsSection />

      {/* ============ ELITE POSING & PRESENTATION SERVICES ============ */}
      <section className="py-24 sm:py-32 lg:py-40 bg-[#050505]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14 sm:mb-20">
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/60 bg-[#D4AF37]/[0.08] px-4 py-1.5 mb-6">
              <Trophy className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-bold">
                Program 01 · Primary Focus
              </span>
            </div>
            <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-4">Services</p>
            <h2
              className="font-display text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-white leading-[0.95] mb-6 tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              THE COMPLETE <span className="text-[#D4AF37]">POSING & PRESENTATION</span> SYSTEM
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              Master every aspect of your stage performance—from mandatory poses to confident stage
              presence. Refined pose by pose, transition by transition.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { icon: Video, label: "Live Video Call Sessions" },
              { icon: Radio, label: "Real-Time Corrections" },
              { icon: Camera, label: "Video Analysis" },
              { icon: Crown, label: "Olympia Prep" },
              { icon: Award, label: "NPC & IFBB Coaching" },
              { icon: Target, label: "Mandatory Poses" },
              { icon: Compass, label: "Quarter Turns" },
              { icon: Users, label: "Stage Walk" },
              { icon: Eye, label: "Stage Presence" },
              { icon: Sparkles, label: "Confidence Coaching" },
              { icon: Star, label: "Individual Routine Choreography" },
              { icon: Timer, label: "Peak Week Adjustments" },
              { icon: Zap, label: "Personalized Feedback" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="group relative bg-white/[0.02] border border-white/[0.08] hover:border-[#D4AF37]/60 hover:bg-white/[0.04] transition-all duration-500 p-5 sm:p-6 backdrop-blur-sm overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(212,175,55,0.35)]"
                data-testid={`service-card-${i}`}
              >
                {/* Gold shimmer on hover */}
                <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-[#D4AF37]/[0.08] via-transparent to-transparent transition-opacity duration-500" />
                <span className="relative flex items-center justify-center w-11 h-11 border border-[#D4AF37]/40 bg-black mb-4 group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-colors duration-300">
                  <s.icon className="w-4 h-4 text-[#D4AF37] group-hover:text-black transition-colors duration-300" />
                </span>
                <p className="relative text-white text-xs sm:text-sm font-medium leading-snug tracking-wide">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY PRESENTATION WINS ============ */}
      <section
        className="relative py-28 sm:py-40 bg-black overflow-hidden"
        data-testid="why-posing-section"
      >
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[#D4AF37]/[0.05] blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-6">The Truth</p>
          <h2
            className="font-display text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.95] mb-10 tracking-tight"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            JUDGES DON'T REWARD <br className="hidden sm:block" />
            THE BEST <span className="text-[#D4AF37]">PHYSIQUE.</span>
            <span className="block mt-3">
              THEY REWARD THE BEST <br className="hidden sm:block" />
              <span className="text-[#D4AF37]">PRESENTATION.</span>
            </span>
          </h2>

          <div className="max-w-2xl mx-auto text-white/70 text-base sm:text-lg leading-relaxed space-y-5">
            <p>
              Many athletes spend years building an incredible physique — but lose valuable placings
              because they don't know how to present it.
            </p>
            <p className="text-white/85">
              Posing is what allows judges to see your physique at its absolute best.
            </p>
            <p className="text-[#D4AF37] italic font-serif text-lg sm:text-xl">
              Every angle. Every transition. Every expression. Every movement matters.
            </p>
          </div>
        </div>
      </section>

      {/* ============ LIVE VIDEO CALL SESSIONS ============ */}
      <section
        className="relative py-24 sm:py-32 bg-[#050505] overflow-hidden"
        data-testid="live-sessions-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06] px-4 py-1.5 mb-6">
              <Video className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-semibold">
                Live Sessions
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.0] mb-6"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              TRAIN LIVE FROM<br />
              <span className="text-[#D4AF37]">ANYWHERE IN THE WORLD</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              Receive live one-on-one coaching through WhatsApp Video, FaceTime, Zoom, or Google Meet.
              During every session we'll work on:
            </p>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-10">
              {[
                "Mandatory Poses",
                "Quarter Turns",
                "Stage Walk",
                "Routine Presentation",
                "Transitions",
                "Peak Week Adjustments",
                "Confidence",
                "Body Positioning",
                "Stage Presence",
              ].map((s) => (
                <div key={s} className="flex items-center gap-2.5 text-sm text-white/80">
                  <Check className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                  <span>{s}</span>
                </div>
              ))}
            </div>

            <Link
              to="/assessment"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold hover:bg-white transition-colors"
              data-testid="live-book-btn"
            >
              Book Your Session
              <ArrowRight size={14} />
            </Link>
          </motion.div>

          {/* Right — Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[380px] sm:h-[460px] lg:h-[520px]"
          >
            <div className="absolute inset-0 rounded-lg overflow-hidden">
              <img
                src="/assets/images/img_5296_chris-coaching-athlete.jpeg"
                alt="Chris Cormier coaching a competitor mid-pose"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </div>
            {/* Glass card */}
            <div className="absolute bottom-6 left-6 right-6 bg-black/60 backdrop-blur-lg border border-white/10 p-4">
              <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-1">Session in progress</p>
              <p className="text-white text-sm font-medium">Mandatory Pose Refinement · Front Double Bicep</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ NUTRITION, TRAINING & CONTEST PREP COACHING ============ */}
      <section
        className="relative py-24 sm:py-32 lg:py-40 bg-black overflow-hidden"
        data-testid="nutrition-training-section"
      >
        {/* Subtle divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
        <div className="pointer-events-none absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/[0.03] blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14 sm:mb-20">
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-black/40 px-4 py-1.5 mb-6">
              <Dumbbell className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-semibold">
                Full Coaching Program
              </span>
            </div>
            <h2
              className="font-display text-[2.25rem] sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[0.95] mb-6 tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              NUTRITION, TRAINING &<br className="hidden sm:block" />{" "}
              <span className="text-[#D4AF37]">CONTEST PREP</span> COACHING
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              For athletes who want complete, end-to-end coaching — from off-season
              conditioning to championship stage.
            </p>
          </div>

          {/* Two-column layout: features + visual */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left — Feature grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="order-2 lg:order-1"
            >
              <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-4">
                What&rsquo;s Included
              </p>
              <h3
                className="font-display text-3xl sm:text-4xl text-white mb-8 leading-tight"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                THE COMPLETE <span className="text-[#D4AF37]">COACHING SYSTEM</span>
              </h3>

              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {[
                  { icon: Apple, title: "Personalized Nutrition", desc: "Custom macros, meal timing, refeeds." },
                  { icon: Dumbbell, title: "Training Programs", desc: "Weekly programming built for your body." },
                  { icon: FlaskConical, title: "Supplementation", desc: "Evidence-based stack guidance." },
                  { icon: Timer, title: "Peak Week Guidance", desc: "Water, sodium, and carb loading dialed in." },
                  { icon: Trophy, title: "Contest Preparation", desc: "Show selection, timeline, backstage plan." },
                  { icon: ClipboardCheck, title: "Weekly Check-Ins", desc: "Direct feedback, tracked adjustments." },
                ].map((it, i) => (
                  <motion.div
                    key={it.title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                    className="group flex items-start gap-3 p-4 bg-white/[0.015] border border-white/[0.08] hover:border-[#D4AF37]/40 hover:bg-white/[0.03] transition-all duration-300"
                    data-testid={`nutrition-feature-${i}`}
                  >
                    <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 border border-[#D4AF37]/40 bg-black group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-colors duration-300">
                      <it.icon className="w-4 h-4 text-[#D4AF37] group-hover:text-black transition-colors duration-300" />
                    </span>
                    <div>
                      <p className="text-white text-sm font-semibold leading-tight mb-0.5">
                        {it.title}
                      </p>
                      <p className="text-white/50 text-[12px] leading-snug">{it.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link
                  to="/coaching-application"
                  className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-[11px] sm:text-xs tracking-[0.25em] uppercase font-bold hover:bg-white transition-all duration-300 shadow-[0_15px_40px_-10px_rgba(212,175,55,0.5)] hover:shadow-[0_18px_48px_-8px_rgba(212,175,55,0.7)]"
                  data-testid="nutrition-apply-btn"
                >
                  <Trophy size={14} />
                  Apply for Coaching
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href={waNutritionLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white px-7 py-4 text-[11px] sm:text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 shadow-[0_15px_40px_-10px_rgba(37,211,102,0.55)] hover:shadow-[0_18px_48px_-8px_rgba(37,211,102,0.75)]"
                  data-testid="nutrition-whatsapp-btn"
                >
                  <WhatsAppIcon size={16} />
                  Ask on WhatsApp
                </a>
              </div>
            </motion.div>

            {/* Right — Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-[400px] sm:h-[480px] lg:h-[560px] order-1 lg:order-2"
            >
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src="/assets/images/tyexuvmo_IMG_5009.jpeg"
                  alt="Chris Cormier coaching a physique athlete through training"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
              </div>
              {/* Corner badge */}
              <div className="absolute top-6 left-6 flex items-center gap-2 bg-[#D4AF37] text-black px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold">
                <Trophy size={12} />
                Program 02
              </div>
              {/* Glass caption */}
              <div className="absolute bottom-6 left-6 right-6 bg-black/70 backdrop-blur-lg border border-white/10 p-5">
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase mb-1.5">
                  Off-Season → Peak Week
                </p>
                <p className="text-white text-sm sm:text-base font-medium leading-snug">
                  Complete coaching from your first training block through
                  <span className="text-[#D4AF37]"> stage day</span>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ WHATSAPP VIDEO SUBMISSION ============ */}
      <section className="py-24 sm:py-32 bg-black" data-testid="video-submission-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 border border-[#25D366]/40 bg-[#25D366]/[0.06] px-4 py-1.5 mb-6">
              <WhatsAppIcon size={14} className="text-[#25D366]" />
              <span className="text-[#25D366] text-[10px] tracking-[0.3em] uppercase font-semibold">
                Simple · Fast · Direct
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.0] mb-4"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              SEND YOUR POSING VIDEOS<br />
              <span className="text-[#D4AF37]">DIRECTLY ON WHATSAPP</span>
            </h2>
            <p className="text-white/60 text-base max-w-2xl mx-auto">
              No complicated portals. No accounts. Just film, send, and get personalized feedback.
            </p>
          </div>

          <div className="bg-white/[0.02] border border-white/[0.08] backdrop-blur-sm p-8 sm:p-12 mb-8">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6">Please Send:</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Front Pose",
                "Back Pose",
                "Side Poses",
                "Quarter Turns",
                "Mandatory Poses",
                "Walk-In",
                "30–60 Second Routine",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 py-2 px-4 bg-black/40 border border-white/5">
                  <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-white/90 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white px-10 py-5 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold transition-colors shadow-[0_15px_45px_-10px_rgba(37,211,102,0.5)]"
              data-testid="wa-submission-btn"
            >
              <WhatsAppIcon size={20} />
              Send My Videos Now
              <ArrowRight size={16} />
            </a>
            <p className="text-white/40 text-xs mt-4 tracking-wider">
              +1 (619) 387-6711 · Reply within business hours
            </p>
          </div>
        </div>
      </section>

      {/* ============ TRANSFORMATION GALLERY ============ */}
      <section className="py-24 sm:py-32 bg-[#050505]" data-testid="transformations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">Coached to the Top</p>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              THE <span className="text-[#D4AF37]">CAMP CORMIER</span> STAGE
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { img: "/assets/images/img_5303_chris-sergio.jpeg", label: "Chris & Sergio Oliva Jr." },
              { img: "/assets/images/img_5311_side-chest-pink.jpeg", label: "Posing Precision" },
              { img: "/assets/images/img_5310_kasey-housmans.jpeg", label: "Kasey Housmans · NPC USA" },
              { img: "/assets/images/img_5326_backstage-coaching.jpeg", label: "Backstage Coaching" },
              { img: "/assets/images/img_5312_back-pose-pink.jpeg", label: "Back Detail" },
              { img: "/assets/images/img_5327_comparison-stage.jpeg", label: "Pro Stage" },
            ].map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08 }}
                className="group relative aspect-[3/4] overflow-hidden bg-[#0A0A0A]"
                data-testid={`gallery-card-${i}`}
              >
                <img src={it.img} alt={it.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-[#D4AF37] text-[10px] tracking-[0.25em] uppercase">{it.label}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/gallery"
              className="inline-flex items-center gap-3 border border-white/20 text-white px-8 py-3 text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors"
            >
              View Full Gallery
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ VIDEO TESTIMONIALS (hidden — add YouTube URLs to activate) ============ */}
      <VideoTestimonialsSection />

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-24 sm:py-32 bg-black" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">Voices From The Stage</p>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              WHAT ATHLETES <span className="text-[#D4AF37]">SAY</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                quote: "Chris changed the way I hit every mandatory. Placed top 3 at my first Pro Qualifier.",
                name: "K. Housmans",
                title: "NPC USA · Classic Physique",
                img: "/assets/images/img_5310_kasey-housmans.jpeg",
              },
              {
                quote: "The peak-week posing session tightened everything. Judges could finally see my back.",
                name: "S. Oliva Jr.",
                title: "IFBB Pro",
                img: "/assets/images/img_5303_chris-sergio.jpeg",
              },
              {
                quote: "Confidence on stage is what set me apart. Chris taught me how to own every second.",
                name: "Camp Cormier Athlete",
                title: "Bikini · Regional",
                img: "/assets/images/img_5296_chris-coaching-athlete.jpeg",
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/[0.02] border border-white/[0.08] backdrop-blur-sm p-6 hover:border-[#D4AF37]/40 transition-colors"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-[#D4AF37]/40" />
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-white/50 text-[10px] tracking-wider uppercase">{t.title}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} size={12} className="text-[#D4AF37]" fill="currentColor" />
                  ))}
                </div>
                <p className="text-white/75 text-sm italic leading-relaxed">"{t.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        className="relative py-32 sm:py-40 bg-gradient-to-b from-black via-[#050505] to-black overflow-hidden"
        data-testid="final-cta-section"
      >
        <div className="pointer-events-none absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.06)_0%,transparent_60%)]" />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-6">The Next Step</p>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-8xl text-white leading-[0.95] mb-8"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            READY TO PRESENT<br />
            YOUR <span className="text-[#D4AF37]">BEST PHYSIQUE?</span>
          </h2>
          <p className="text-white/65 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
            Whether you're preparing for your first NPC show, a Pro Qualifier, or the Olympia stage,
            Camp Cormier delivers elite coaching and posing designed to maximize your presentation.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1FB855] text-white px-10 py-4 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold transition-colors shadow-[0_15px_45px_-10px_rgba(37,211,102,0.5)]"
              data-testid="final-whatsapp-btn"
            >
              <WhatsAppIcon size={18} />
              Chat on WhatsApp
            </a>
            <Link
              to="/assessment"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold hover:bg-white transition-colors"
              data-testid="final-book-btn"
            >
              <Trophy size={16} />
              Book Your Session Today
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

/**
 * VIDEO TESTIMONIALS — hidden until YouTube URLs are provided.
 * To activate: populate the `VIDEO_TESTIMONIALS` array with objects
 * { youtubeId: "abc123", name: "Athlete", title: "NPC USA · 1st" }.
 * The section auto-renders when the array is non-empty.
 */
const VIDEO_TESTIMONIALS = [
  // Example (leave commented until URLs are provided):
  // { youtubeId: "dQw4w9WgXcQ", name: "K. Housmans", title: "NPC USA · Classic Physique" },
];

const VideoTestimonialsSection = () => {
  if (!VIDEO_TESTIMONIALS.length) return null;
  return (
    <section className="py-24 sm:py-32 bg-black" data-testid="video-testimonials-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-14">
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-4">
            Real Athletes · Real Results
          </p>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            VIDEO <span className="text-[#D4AF37]">TESTIMONIALS</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VIDEO_TESTIMONIALS.map((v, i) => (
            <div key={i} className="aspect-video bg-black border border-white/10">
              <iframe
                src={`https://www.youtube.com/embed/${v.youtubeId}`}
                title={v.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
              <div className="p-3 border-t border-white/5">
                <p className="text-white text-sm font-semibold">{v.name}</p>
                <p className="text-white/50 text-[10px] tracking-wider uppercase">{v.title}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
